import classNames from 'classnames';
import React, { LegacyRef, useEffect, useMemo, useRef } from 'react';
import { useMeasure, useUpdateEffect } from 'react-use';
import { useEditorStore } from './editor-store';
import { AnimatedBlocksView } from './animated-blocks-view';
import { findBlock } from './helpers/find-block';
import { ContextMenuContainer } from './context-menu-container';
import { BlockContextMenuContent } from './block-context-menu-content';
import { SectionCardContextMenuContent } from './section-card-context-menu-content';
import { SectionDividerContextMenuContent } from './section-divider-context-menu-content';
import { EmptySectionContextMenuContent } from './empty-section-context-menu-content';
import { MutualActionPlanItemContextMenuContent } from './mutual-action-plan-item-context-menu-content';
import { useFeatureFlag } from './feature-flags-store';
import { MutualActionPlanItemAssigneeContextMenuContent } from './mutual-action-plan-item-assignee-context-menu-content';
import { findESignatureBlockId, isReadonly } from './native-e-signature/utils';
import { CanvasContextMenu } from './canvas-context-menu';
import { getRelativeCanvasPosition } from './utils/get-relative-canvas-position';
import { useApplyHighlightTheme } from './utils/apply-highlight-theme.hook';
import { shallow } from 'zustand/shallow';

const MemoizedAnimatedBlocksView = React.memo(AnimatedBlocksView);

export const NeueEditorContent = () => {
  const debugEditorAreas = useFeatureFlag('debug_editor_areas');
  const sections = useEditorStore((state) => state.layout.sections);
  const layoutReady = useEditorStore((state) => state.layout.layoutReady);
  const layoutStage = useEditorStore((state) => state.layoutStage);
  const contextMenuState = useEditorStore((state) => state.contextMenuState);
  const setContainerRects = useEditorStore((state) => state.setContainerRects);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
  const setCanvasScrollPosition = useEditorStore((state) => state.setCanvasScrollPosition);
  const innerActualHeight = useEditorStore((state) => state.layout.innerActualHeight);
  const setScrollToBlockId = useEditorStore((state) => state.setScrollToBlockId);
  const selectBlock = useEditorStore((state) => state.selectBlock);
  const selectedBlockId = useEditorStore((state) => state.selectedBlockId);
  const isLayoutReady = useEditorStore((state) => state.layoutStage === 'ready', shallow);

  const readonly = isReadonly(sections);
  const nativeESignatureSignedBlockId = findESignatureBlockId(sections);

  const setContextMenuState = useEditorStore((state) => state.setContextMenuState);
  const sectionDrawerOpen = useEditorStore((state) => state.sectionDrawerOpen);

  const [editorContentLegacyRef, editorContentRect] = useMeasure();
  const [editorContentEl, setEditorContentEl] = React.useState<HTMLDivElement | null>(null);

  const [innerLegacyRef, innerRect] = useMeasure();
  const innerRef = useRef<HTMLDivElement>();

  const isInnerElementTrulyRendered = useMemo(() => innerRect.height > 0, [innerRect.height]);

  const themeSettings = useEditorStore((state) => state.themeSettings, shallow);

  useApplyHighlightTheme({ themeSettings, containerElement: editorContentEl, isLayoutReady });

  useUpdateEffect(() => {
    if (!editorContentEl) return;
    if (!isInnerElementTrulyRendered) return;
    // innerElement is rendered, the upper elements must be rendered too
    const innerElement = innerRef.current!;
    const innerRect = innerElement.getBoundingClientRect();

    const editorContentRect = editorContentEl.getBoundingClientRect();

    const outerLeft = Math.max(0, (editorContentRect.width - innerRect.width) / 4);
    const outerRight = Math.max(0, (editorContentRect.width - innerRect.width) / 4);
    const outerWidth = editorContentRect.width - outerLeft - outerRight;

    setContainerRects(
      {
        editorContentRect: editorContentRect,
        outerAreaRect: {
          x: outerLeft,
          y: 0,
          width: outerWidth,
          height: editorContentRect.height, // Note, need this to be the height of the editorContent, not the outer area
        } as DOMRect,
        innerAreaRect: {
          x: innerRect.x,
          y: 0,
          width: innerRect.width,
          height: editorContentRect.height, // Note, need this to be the height of the editorContent, not the inner area
        } as DOMRect,
      },
      editorContentEl
    );
  }, [editorContentEl, isInnerElementTrulyRendered, innerRect, editorContentRect]);

  React.useEffect(() => {
    if (!editorContentEl) return;
    const handleScroll = () => {
      setCanvasScrollPosition(editorContentEl.scrollLeft, editorContentEl.scrollTop);
    };
    editorContentEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      editorContentEl.removeEventListener('scroll', handleScroll);
    };
  }, [editorContentEl]);

  const onEditorContentClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target === innerRef.current || e.target === editorContentEl) {
      clearSelectedBlock();
    }
  };

  useEffect(() => {
    if (readonly && nativeESignatureSignedBlockId && selectedBlockId !== nativeESignatureSignedBlockId) {
      setTimeout(() => {
        setScrollToBlockId(nativeESignatureSignedBlockId);
        selectBlock(nativeESignatureSignedBlockId);
      }, 300);
    }
  }, [nativeESignatureSignedBlockId, selectedBlockId]);

  const renderContextMenu = () => {
    if (readonly) {
      return null;
    }
    if (!contextMenuState) {
      return null;
    }
    let content = null;
    if (contextMenuState.entity.type === 'block') {
      const blockId = contextMenuState.entity.blockId;
      const block = findBlock(sections, blockId);
      if (block) {
        content = <BlockContextMenuContent block={block} />;
      }
    } else if (contextMenuState.entity.type === 'section-card') {
      const sectionId = contextMenuState.entity.sectionId;
      const items = contextMenuState.entity.items;
      const section = sections.find((s) => s.id === sectionId);

      if (section) {
        content = (
          <SectionCardContextMenuContent
            sectionIds={sections.map((section) => section.id)}
            section={section}
            friendlyPath={section.friendlyPath}
            renameAction={contextMenuState.entity.renameAction}
            items={items}
          />
        );
      }
    } else if (contextMenuState.entity.type === 'section-divider') {
      const { previousSectionId, nextSectionId } = contextMenuState.entity;
      if (previousSectionId && nextSectionId) {
        content = (
          <SectionDividerContextMenuContent previousSectionId={previousSectionId} nextSectionId={nextSectionId} />
        );
      }
    } else if (contextMenuState.entity.type === 'canvas') {
      content = <CanvasContextMenu />;
    } else if (contextMenuState.entity.type === 'empty-section') {
      const sectionId = contextMenuState.entity.sectionId;
      const section = sections.find((s) => s.id === sectionId);
      if (section) {
        content = <EmptySectionContextMenuContent section={section} />;
      }
    } else if (contextMenuState.entity.type === 'mutual-action-plan-item') {
      const { item, mutualActionPlan } = contextMenuState.entity;
      content = <MutualActionPlanItemContextMenuContent item={item} mutualActionPlan={mutualActionPlan} />;
    } else if (contextMenuState.entity.type === 'mutual-action-plan-item-assignee') {
      const { item, mutualActionPlan, assignee } = contextMenuState.entity;
      content = (
        <MutualActionPlanItemAssigneeContextMenuContent
          item={item}
          mutualActionPlan={mutualActionPlan}
          assignee={assignee}
        />
      );
    }

    if (!content) {
      return null;
    }
    return (
      <ContextMenuContainer
        style={{
          top: `${contextMenuState.pageY}px`,
          left: `${contextMenuState.pageX}px`,
        }}
      >
        {content}
      </ContextMenuContainer>
    );
  };

  return (
    <div
      className={classNames(
        'relative w-full h-full overflow-y-auto overflow-x-hidden transition-opacity flex justify-center',
        {
          'opacity-0': layoutStage !== 'ready',
          'opacity-100': layoutStage === 'ready',
          'bg-red-400/20': debugEditorAreas,
        }
      )}
      ref={
        ((element: HTMLDivElement) => {
          editorContentLegacyRef(element);
          setEditorContentEl(element);
        }) as LegacyRef<HTMLDivElement>
      }
      onClick={onEditorContentClick}
      onContextMenu={(e) => {
        let { pageX, pageY } = e;
        e.preventDefault();
        setContextMenuState({
          entity: { type: 'canvas' },
          ...getRelativeCanvasPosition(pageX, pageY, 136),
        });
      }}
    >
      {readonly && (
        <div
          className='absolute inset-0 bg-neue-canvas-fg-20 w-full z-[154]'
          style={{ height: innerActualHeight || 0 }}
        ></div>
      )}
      <div
        className={classNames('shrink-0 transition-[width]', {
          'w-[88px]': !sectionDrawerOpen,
          'w-[302px]': sectionDrawerOpen,
        })}
      ></div>
      <div
        className={classNames('flex mx-auto relative max-w-[1440px] w-2/3 min-w-[720px]', {
          'bg-green-400/20': debugEditorAreas,
        })}
        ref={
          ((element: HTMLDivElement) => {
            innerRef.current = element;
            innerLegacyRef(element);
          }) as LegacyRef<HTMLDivElement>
        }
      >
        {layoutReady && <MemoizedAnimatedBlocksView />}
      </div>
      <div className='shrink-0 w-6' />
      {renderContextMenu()}
    </div>
  );
};
